import React, { Fragment, useEffect, useState, useRef } from "react";
import ServicePlanAPI from '~/api/servicePlanApi';
import api from "~/api/api";
import { useFlashBar } from '~/contexts/FlashBarContext';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes, faTimesCircle, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { CenteredModal, AddCardForm } from '~/components';
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import styles from './UpgradePlanModal.module.scss';
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || "pk_test_placeholder");

function UpgradePlanModal({ show, onClose, initialData }) {
    const { showFlash } = useFlashBar();
    const [serviceType, setServiceType] = useState(1); // 1 = Personal, 2 = Business
    const [servicePlans, setServicePlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
    const [isLoadingPaymentInfo, setIsLoadingPaymentInfo] = useState(true);

    const [showAddModal, setShowAddModal] = useState(false);
    const [clientSecret, setClientSecret] = useState(null);
    const [isCreatingIntent, setIsCreatingIntent] = useState(false);

    // New states for plan change confirmation
    const [showConfirmPlanChange, setShowConfirmPlanChange] = useState(false);
    const [confirmPlanData, setConfirmPlanData] = useState({ text: '' });
    const [freePlanStatus, setFreePlanStatus] = useState(null);

    const handleOpenAddModal = async () => {
        try {
            setIsCreatingIntent(true);
            const res = await api.post('/user/stripe-payments/create-setup-intent');
            setClientSecret(res.data.ClientSecret);
            setShowAddModal(true);
        } catch (err) {
            showFlash({ type: 'error', message: 'Failed to initialize card setup.' });
        } finally {
            setIsCreatingIntent(false);
        }
    };

    const latestPlanIdRef = useRef(selectedPlanId);
    const latestServiceTypeRef = useRef(serviceType);

    useEffect(() => {
        latestPlanIdRef.current = selectedPlanId;
        latestServiceTypeRef.current = serviceType;
    }, [selectedPlanId, serviceType]);

    const handleSaveSuccess = async () => {
        setShowAddModal(false);
        setClientSecret(null);
        setHasPaymentMethod(true);
        
        if (initialData?.autoOpenAddPayment) {
            const planId = latestPlanIdRef.current || initialData.planId;
            const sType = latestServiceTypeRef.current || initialData.serviceType;
            if (planId && sType) {
                setIsProcessing(true);
                try {
                    const response = await api.post(`/user/stripe-payments/create-checkout-session/${planId}/${sType}`);
                    onClose();
                    window.location.href = response.data.Url;
                } catch (error) {
                    setIsProcessing(false);
                }
            }
        }
    };

    useEffect(() => {
        if (show) {
            // Apply initialData if provided
            if (initialData) {
                if (initialData.serviceType && initialData.serviceType !== serviceType) {
                    setServiceType(initialData.serviceType);
                }
            }

            // Check payment methods
            setIsLoadingPaymentInfo(true);
            api.get('/user/stripe-payments/payment-methods')
                .then(res => {
                    setHasPaymentMethod(res.data && res.data.length > 0);
                })
                .catch(err => console.error("Error fetching payment methods", err))
                .finally(() => setIsLoadingPaymentInfo(false));

            ServicePlanAPI.checkExistFreePlan()
                .then((res) => {
                    setFreePlanStatus(res.data?.Status ?? null);
                })
                .catch(err => console.error("Error checking free plan status", err));

            ServicePlanAPI.getByServiceType(initialData?.serviceType || serviceType)
                .then((res) => {
                    setServicePlans(res.data);
                    
                    let targetPlanId = selectedPlanId;
                    if (initialData && initialData.planId) {
                        targetPlanId = initialData.planId;
                    }

                    if (res.data.length > 0 && !targetPlanId) {
                        const firstEligible = res.data.find(p => !(p.Price === 0 && (freePlanStatus === 1 || freePlanStatus === 2)));
                        setSelectedPlanId(firstEligible ? firstEligible.ServicePlanId : res.data[0].ServicePlanId);
                    } else if (res.data.length > 0) {
                        const exists = res.data.find(p => p.ServicePlanId === targetPlanId);
                        if (!exists) {
                            const firstEligible = res.data.find(p => !(p.Price === 0 && (freePlanStatus === 1 || freePlanStatus === 2)));
                            setSelectedPlanId(firstEligible ? firstEligible.ServicePlanId : res.data[0].ServicePlanId);
                        } else {
                            setSelectedPlanId(targetPlanId);
                        }
                    }
                })
                .catch((err) => {
                    console.error("Error fetching service plans", err);
                });
        }
    }, [serviceType, show, initialData]); // Note: freePlanStatus is intentionally omitted to avoid infinite loops, we handle its update below

    useEffect(() => {
        if ((freePlanStatus === 1 || freePlanStatus === 2) && servicePlans.length > 0) {
            const currentPlan = servicePlans.find(p => p.ServicePlanId === selectedPlanId);
            if (currentPlan && currentPlan.Price === 0) {
                const firstPaid = servicePlans.find(p => p.Price > 0);
                if (firstPaid) {
                    setSelectedPlanId(firstPaid.ServicePlanId);
                }
            }
        }
    }, [freePlanStatus, servicePlans, selectedPlanId]);

    const autoCheckoutInitiated = useRef(false);

    useEffect(() => {
        if (!show) {
            autoCheckoutInitiated.current = false;
        }
    }, [show]);

    useEffect(() => {
        if (show && initialData?.autoOpenAddPayment && !isLoadingPaymentInfo) {
            if (!hasPaymentMethod && !showAddModal) {
                handleOpenAddModal();
            } else if (hasPaymentMethod && !autoCheckoutInitiated.current) {
                autoCheckoutInitiated.current = true;
                const planId = initialData.planId || selectedPlanId;
                const sType = initialData.serviceType || serviceType;
                if (planId && sType) {
                    setIsProcessing(true);
                    api.post(`/user/stripe-payments/create-checkout-session/${planId}/${sType}`)
                        .then(response => {
                            onClose();
                            window.location.href = response.data.Url;
                        })
                        .catch(error => {
                            setIsProcessing(false);
                        });
                }
            }
        }
    }, [show, initialData, isLoadingPaymentInfo, hasPaymentMethod, showAddModal, selectedPlanId, serviceType]);

    const handleSelectPlan = (id) => {
        setSelectedPlanId(id);
    };

    const handleContinue = async (forceBypassPaymentCheck = false) => {
        if (!selectedPlanId) return;

        const selectedService = servicePlans.find(p => p.ServicePlanId === selectedPlanId);
        
        // Handle Free Plan (ID = 1, usually)
        if (selectedPlanId === 1 || (selectedService && selectedService.Price === 0)) {
            setIsProcessing(true);
            ServicePlanAPI.checkExistFreePlan()
                .then((res) => {
                    const status = res.data.Status;
                    if (status === 0) { // Eligible
                        ServicePlanAPI.confirmFreePlan()
                            .then(() => {
                                showFlash({ type: 'success', message: 'Free plan activated successfully!' });
                                window.location.href = '/ai-chatbot/chat';
                            })
                            .catch((err) => {
                                const errMsg = err.response?.data?.Message || "An error occurred while registering the free plan.";
                                showFlash({ type: 'error', message: errMsg });
                            })
                            .finally(() => setIsProcessing(false));
                    } else if (status === 1) { // Used Free Plan
                        showFlash({ type: 'error', message: 'You have already used the Free plan. Please select a paid plan.' });
                        setIsProcessing(false);
                    } else if (status === 2) { // Active Paid Plan
                        showFlash({ type: 'error', message: 'You currently have an active paid plan. You cannot switch to the Free plan until your current plan expires.' });
                        setIsProcessing(false);
                    }
                })
                .catch(() => {
                    showFlash({ type: 'error', message: 'Could not check free plan status.' });
                    setIsProcessing(false);
                });
            return;
        }

        // Handle Paid Plan
        if (!hasPaymentMethod && !forceBypassPaymentCheck) {
            handleOpenAddModal();
            return;
        }

        const proceedToCheckout = async () => {
            setIsProcessing(true);
            try {
                const response = await api.post(`/user/stripe-payments/create-checkout-session/${selectedPlanId}/${serviceType}`);
                onClose();
                window.location.href = response.data.Url;
            } catch (error) {
                setIsProcessing(false);
            }
        };

        // Check if user has an active paid plan before checking out
        setIsProcessing(true);
        ServicePlanAPI.checkExistFreePlan()
            .then((res) => {
                setIsProcessing(false);
                if (res.data && res.data.Status === 2) {
                    setConfirmPlanData({
                        text: `You are currently using the ${res.data.ActivePlanName || 'Paid'} plan (expires ${new Date(res.data.ActivePlanEndDate).toLocaleDateString()}). If you continue to purchase a new plan, your current plan and its remaining time will be canceled. Do you want to proceed?`
                    });
                    setShowConfirmPlanChange(true);
                } else {
                    proceedToCheckout();
                }
            })
            .catch(() => {
                setIsProcessing(false);
                proceedToCheckout();
            });
    };

    const selectedService = servicePlans.find(p => p.ServicePlanId === selectedPlanId);

    return (
        <Fragment>
            {(!initialData?.autoOpenAddPayment) && (
                <CenteredModal 
                    show={show} 
                    onClose={onClose} 
                    modalType="modal-xl"
                    modalContentClass={cx('custom-modal-content')}
                    title=""
                    closeText=""
                >
                    <div className="p-0 position-relative" style={{ filter: showAddModal ? 'blur(4px) brightness(0.9)' : 'none', transition: 'all 0.2s ease', pointerEvents: showAddModal ? 'none' : 'auto' }}>
                        <button 
                            onClick={onClose} 
                            className="btn btn-link position-absolute top-0 end-0 p-3 text-dark z-3"
                            style={{ zIndex: 10 }}
                        >
                            <FontAwesomeIcon icon={faTimes} className="fs-6 opacity-75" />
                        </button>
                        <div className="row g-0">
                            {/* Left Column: Plan Selection */}
                            <div className="col-lg-7 p-4 p-md-5 bg-white rounded-start-4">
                        <h4 className="fw-bolder mb-4">
                            Upgrade to the {selectedService ? selectedService.Name : ''} plan to access more features
                        </h4>
                        
                        {/* Tabs for Personal / Business */}
                        <ul className="nav nav-pills mb-4" id="pills-tab" role="tablist">
                            <li className="nav-item" role="presentation">
                                <button 
                                    className={`nav-link rounded-pill px-4 py-2 fs-9 fw-bold ${serviceType === 1 ? 'active bg-primary text-white' : 'text-body-tertiary'}`} 
                                    onClick={() => setServiceType(1)}
                                >
                                    Personal
                                </button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button 
                                    className={`nav-link rounded-pill px-4 py-2 fs-9 fw-bold ${serviceType === 2 ? 'active bg-primary text-white' : 'text-body-tertiary'}`} 
                                    onClick={() => setServiceType(2)}
                                >
                                    Business
                                </button>
                            </li>
                        </ul>

                        <div className="d-flex flex-column gap-3 mb-5">
                            {servicePlans.map((plan) => {
                                const isFreePlan = plan.Price === 0;
                                const isDisabled = isFreePlan && (freePlanStatus === 1 || freePlanStatus === 2);
                                return (
                                <div 
                                    key={plan.ServicePlanId}
                                    className={cx('plan-card', { 'selected': selectedPlanId === plan.ServicePlanId, 'opacity-50': isDisabled })}
                                    onClick={() => { if (!isDisabled) handleSelectPlan(plan.ServicePlanId); }}
                                    style={isDisabled ? { cursor: 'not-allowed', backgroundColor: '#f8f9fa' } : {}}
                                >
                                    <div className="d-flex align-items-center">
                                        <div className={cx('radio-btn', { 'checked': selectedPlanId === plan.ServicePlanId })}>
                                            <div className={cx('radio-inner')}></div>
                                        </div>
                                        <div className="ms-3 w-100">
                                            <h6 className="m-0 fw-bold d-flex align-items-center gap-2">
                                                {plan.Name} 
                                                <span className="fs-9 fw-bold text-dark">${plan.Price}</span> 
                                                <span className="text-body-tertiary fs-10 fw-normal">per {plan.DurationUnit.toLowerCase()}</span>
                                            </h6>
                                            <p className="m-0 fs-9 text-body-secondary mt-1">{plan.Description}</p>
                                            {isDisabled && (
                                                <div className="text-danger mt-1 bg-danger bg-opacity-10 d-inline-block px-2 py-1 rounded-2" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                                    {freePlanStatus === 1 ? "You have already used the FREE plan." : "You currently have an active paid plan."}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )})}
                        </div>

                        {selectedService && (
                            <button 
                                className={cx('btn-continue', { 'btn-warning': !hasPaymentMethod && selectedService.Price > 0 })} 
                                onClick={handleContinue}
                                disabled={isProcessing || isLoadingPaymentInfo || isCreatingIntent}
                            >
                                {isProcessing || isLoadingPaymentInfo || isCreatingIntent
                                    ? <Fragment><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Processing...</Fragment>
                                    : (!hasPaymentMethod && selectedService.Price > 0) 
                                        ? 'Add Payment Method First' 
                                        : `Continue with ${selectedService.Name} Plan`}
                            </button>
                        )}
                        <p className="text-body-tertiary fs-10 mt-3 mb-0">Pricing reflects standard plans. Subscriptions renew automatically.</p>
                    </div>

                    {/* Right Column: Features List */}
                    <div className="col-lg-5 p-4 p-md-5 bg-light rounded-end-4 border-start d-flex flex-column justify-content-center">
                        <h5 className="fw-bold mb-4 fs-8">
                            {selectedService ? `${selectedService.Name} plan includes:` : 'Plan features:'}
                        </h5>
                        {selectedService && (
                            <ul className="list-unstyled m-0">
                                <li className="mb-3 d-flex align-items-start gap-3">
                                    <FontAwesomeIcon icon={faCheck} className="text-primary mt-1" />
                                    <span className="fs-9 text-dark fw-medium">Time Limit: {selectedService.DurationValue} {selectedService.DurationUnit}</span>
                                </li>
                                {selectedService.TypeId === 2 && (
                                    <li className="mb-3 d-flex align-items-start gap-3">
                                        <FontAwesomeIcon icon={faCheck} className="text-primary mt-1" />
                                        <span className="fs-9 text-dark fw-medium">User Limit: {selectedService.UserLimit} Users</span>
                                    </li>
                                )}
                                {selectedService.PlanDescriptionMappings.map((feature, index) => (
                                    <li key={index} className="mb-3 d-flex align-items-start gap-3">
                                        {feature.IsApply ? (
                                            <FontAwesomeIcon icon={faCheck} className="text-primary mt-1" />
                                        ) : (
                                            <FontAwesomeIcon icon={faTimes} className="text-body-tertiary mt-1 opacity-50" />
                                        )}
                                        <span className={`fs-9 ${feature.IsApply ? 'text-dark fw-medium' : 'text-body-tertiary'}`}>
                                            {feature.Description}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </CenteredModal>
            )}
        
        <CenteredModal
            show={showAddModal}
            zIndex={1070}
            onClose={() => {
                setShowAddModal(false);
                if (initialData?.autoOpenAddPayment) {
                    onClose();
                }
            }}
            title="Add Payment Method"
            closeText=""
        >
            {clientSecret ? (
                <div className="px-2 pt-2 pb-1">
                    {initialData?.autoOpenAddPayment && (
                        <div className="alert alert-primary border-0 bg-primary-subtle text-primary-emphasis d-flex align-items-center mb-4 rounded-3 p-3" style={{ fontSize: '13px' }}>
                            <FontAwesomeIcon icon={faCircleInfo} className="me-3 fs-5" />
                            <div>
                                To activate your <strong>{selectedService ? selectedService.Name : "selected"}</strong> plan, please provide a secure payment method. Your card will not be charged until you confirm the subscription in the next step. You can securely manage or remove your payment methods at any time from your Profile Settings.
                            </div>
                        </div>
                    )}
                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <AddCardForm
                        clientSecret={clientSecret}
                        onSaveSuccess={handleSaveSuccess}
                        onCancel={() => {
                            setShowAddModal(false);
                            if (initialData?.autoOpenAddPayment) {
                                onClose();
                            }
                        }}
                    />
                </Elements>
                </div>
            ) : (
                <div className="text-center p-4"><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Loading...</div>
            )}
        </CenteredModal>

        <CenteredModal
            show={showConfirmPlanChange}
            zIndex={1070}
            title="Confirm Plan Change"
            onClose={() => !isProcessing && setShowConfirmPlanChange(false)}
            onConfirm={() => {
                setShowConfirmPlanChange(false);
                const proceedToCheckout = async () => {
                    setIsProcessing(true);
                    try {
                        const response = await api.post(`/user/stripe-payments/create-checkout-session/${selectedPlanId}/${serviceType}`);
                        onClose();
                        window.location.href = response.data.Url;
                    } catch (error) {
                        setIsProcessing(false);
                    }
                };
                proceedToCheckout();
            }}
            isSubmitting={isProcessing}
            confirmText="Yes, proceed to payment!"
            closeText="Cancel"
        >
            <div className="p-3 text-center">
                <div 
                    className="mb-3 mx-auto d-flex align-items-center justify-content-center rounded-circle" 
                    style={{ 
                        width: '60px', 
                        height: '60px', 
                        background: '#fff3cd', 
                        color: '#ffc107' 
                    }}
                >
                    <FontAwesomeIcon icon={faTimesCircle} className="fs-3" />
                </div>
                <h5 className="fw-bold mb-2">Confirm Plan Change</h5>
                <p className="text-muted fs-8-1 mb-0">{confirmPlanData.text}</p>
            </div>
        </CenteredModal>

        </Fragment>
    );
}

export default UpgradePlanModal;
